/**
 * Public chain scorecard page.
 *
 * Mirrors the per-place CardView structure component-for-component so
 * a chain scorecard reads as "a scorecard whose subject is a chain
 * instead of a place". Same outer `<Container header={<Header>}>`,
 * same `<ScorecardHeader>` identity card (with a synthesized
 * ``PlaceBasic`` for the chain), same `<SegmentedControl>` range
 * switcher, same `<Tabs>` source picker, same `<StatisticBox>` grid,
 * same `<KeywordList>`, same `<ReviewList>`.
 *
 * URL: ``/chain/{slug_or_uuid}`` — unauth, anyone with the link views.
 */
import {
  Container,
  Box,
  Button,
  Header,
  Grid,
  Link,
  SpaceBetween,
  SegmentedControl,
  SegmentedControlProps,
  Icon,
  Tabs,
} from '@cloudscape-design/components';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { ViewLayout } from '../../components/ViewLayout';
import { StatisticBox } from '../../components/StatisticBox';
import { KeywordList } from '../../components/KeywordList';
import { ReviewList } from '../../components/ReviewList';
import { ScorecardHeader } from '../../components/ScorecardHeader';
import { SourceRatingsSummary } from '../../components/SourceRatingsSummary';
import { LoadingOrError } from '../../components/Loading';
import ReportCardHeader from '../../components/ReportCardHeader';
import { ReportDownloadButton } from '../../components/ReportDownloadButton';
import { ScorecardOpportunities } from '../../components/ScorecardOpportunities';
import { GoogleIcon } from '../../common/icons/GoogleIcon';
import { YelpIcon } from '../../common/icons/YelpIcon';
import { BBBIcon } from '../../common/icons/BBBIcon';
import { DealerRaterIcon } from '../../common/icons/DealerRaterIcon';
import { OpenTableIcon } from '../../common/icons/OpenTableIcon';
import { TripAdvisorIcon } from '../../common/icons/TripAdvisorIcon';
import { CarsComIcon } from '../../common/icons/CarsComIcon';
import { CarFaxIcon } from '../../common/icons/CarFaxIcon';
import { EdmundsIcon } from '../../common/icons/EdmundsIcon';
import { CarGurusIcon } from '../../common/icons/CarGurusIcon';
import { BookingIcon } from '../../common/icons/BookingIcon';
import { ExpediaIcon } from '../../common/icons/ExpediaIcon';
import { GolfPassIcon } from '../../common/icons/GolfPassIcon';
import { downloadChainReportPdf, getChainReport } from '../../common/api/chains';
import type {
  PlaceBasic,
  PlaceSourceSnapshot,
  ReviewBasic,
  ReviewKeyword,
  StatisticStatus,
} from '../../common/types';
import { getRatingTrend, getScorecardOpportunities } from '../../common/scorecardInsights';

// Range options — same set the place CardView offers.
const ALL_RANGE_OPTIONS: SegmentedControlProps.Option[] = [
  { id: '30-days', text: '30 Days' },
  { id: '60-days', text: '60 Days' },
  { id: '90-days', text: '90 Days' },
  { id: '180-days', text: '180 Days' },
];

const rangeIdToDays: Record<string, number> = {
  '30-days': 30,
  '60-days': 60,
  '90-days': 90,
  '180-days': 180,
};

// Source key → (display text, icon SVG). Same icon set the per-place
// view uses for its <Tabs> source tabs.
const SOURCE_META: Record<string, { text: string; iconSvg: React.ReactNode }> = {
  google: { text: 'Google', iconSvg: <GoogleIcon /> },
  yelp: { text: 'Yelp', iconSvg: <YelpIcon /> },
  bbb: { text: 'BBB', iconSvg: <BBBIcon /> },
  dealerrater: { text: 'DealerRater', iconSvg: <DealerRaterIcon /> },
  carscom: { text: 'Cars.com', iconSvg: <CarsComIcon /> },
  carfax: { text: 'CarFax', iconSvg: <CarFaxIcon /> },
  edmunds: { text: 'Edmunds', iconSvg: <EdmundsIcon /> },
  cargurus: { text: 'CarGurus', iconSvg: <CarGurusIcon /> },
  opentable: { text: 'OpenTable', iconSvg: <OpenTableIcon /> },
  tripadvisor: { text: 'TripAdvisor', iconSvg: <TripAdvisorIcon /> },
  booking: { text: 'Booking.com', iconSvg: <BookingIcon /> },
  expedia: { text: 'Expedia', iconSvg: <ExpediaIcon /> },
  golfpass: { text: 'GolfPass', iconSvg: <GolfPassIcon /> },
};

const SOURCE_ORDER = [
  'google',
  'yelp',
  'bbb',
  'dealerrater',
  'opentable',
  'tripadvisor',
  'carscom',
  'carfax',
  'edmunds',
  'cargurus',
  'booking',
  'expedia',
  'golfpass',
];

export default function ChainPublicPage() {
  const { slugOrUuid } = useParams({ from: '/chain/$slugOrUuid' });

  const reportQuery = useQuery({
    queryKey: ['chain-public', slugOrUuid],
    queryFn: () => getChainReport(slugOrUuid),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  // ----- snapshot map (source → ChainReportSourceSnapshot, structurally
  // compatible with PlaceSourceSnapshot for ScorecardHeader's purposes) ---
  const snapshotMap = useMemo(() => {
    const map = new Map<string, PlaceSourceSnapshot>();
    for (const s of reportQuery.data?.source_snapshots ?? []) {
      // ChainReportSourceSnapshot has {source, rating, review_count} —
      // ScorecardHeader only reads those three so the cast is safe.
      map.set(s.source, s as unknown as PlaceSourceSnapshot);
    }
    return map;
  }, [reportQuery.data?.source_snapshots]);

  // ----- Range options (disabled if no period for current source) -----
  const [selectedRange, setSelectedRange] = useState<string>('180-days');
  const [selectedSource, setSelectedSource] = useState<string | null>(null);

  const sourceOptions = useMemo(() => {
    const present = new Set<string>();
    for (const p of reportQuery.data?.periods ?? []) {
      if (p.source) present.add(p.source);
    }
    const sorted = [...present].sort((a, b) => {
      const ai = SOURCE_ORDER.indexOf(a);
      const bi = SOURCE_ORDER.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
    return sorted.map((s) => ({
      id: s,
      text: SOURCE_META[s]?.text ?? s,
      iconSvg: SOURCE_META[s]?.iconSvg ?? null,
      disabled: false,
    }));
  }, [reportQuery.data?.periods]);

  const rangeOptions = useMemo(() => {
    return ALL_RANGE_OPTIONS.map((option) => {
      const hasPeriod = (reportQuery.data?.periods ?? []).some(
        (p) => p.range === option.id && p.source === selectedSource,
      );
      return {
        ...option,
        disabled: !hasPeriod,
        disabledReason: !hasPeriod ? 'No data' : undefined,
      };
    });
  }, [reportQuery.data?.periods, selectedSource]);

  // Auto-pick first available source
  useEffect(() => {
    if (!selectedSource && sourceOptions.length > 0) {
      setSelectedSource(sourceOptions[0].id);
    }
  }, [sourceOptions, selectedSource]);

  // Auto-snap range to one that has data when source changes
  useEffect(() => {
    const cur = rangeOptions.find((o) => o.id === selectedRange);
    if (cur?.disabled) {
      const first = rangeOptions.find((o) => !o.disabled);
      if (first) setSelectedRange(first.id);
    }
  }, [rangeOptions, selectedRange]);

  // Hooks computed BEFORE early-return so React's hooks order is stable.
  // `r` may be undefined while loading — every derived value tolerates
  // that and the early-return below handles the loading/error path.
  const r = reportQuery.data;
  const period = r?.periods.find((p) => p.range === selectedRange && p.source === selectedSource);
  const filteredReviews: ReviewBasic[] = useMemo(() => {
    if (!period || !r) return [];
    return (r.reviews ?? []).filter(
      (rev) =>
        rev.review_date &&
        rev.review_date >= period.start_date &&
        rev.review_date <= period.end_date &&
        rev.source === period.source &&
        rev.content,
    ) as ReviewBasic[];
  }, [r, period]);

  if (reportQuery.isLoading || !r || reportQuery.error) {
    return (
      <ViewLayout
        header={<ReportCardHeader />}
        contentType="dashboard"
        navigationHide
        headerVariant="high-contrast"
      >
        <LoadingOrError loading={reportQuery.isLoading} error={reportQuery.error as Error | null} />
      </ViewLayout>
    );
  }

  // From here on, `r` is non-null. Derive display values.
  const currentRange = rangeOptions.find((o) => o.id === selectedRange) ?? rangeOptions[0];
  const currentSource = sourceOptions.find((o) => o.id === selectedSource) ?? sourceOptions[0];
  const locationItems = r.place_links ?? [];

  // Synthesize a PlaceBasic-shaped object for ScorecardHeader. It only
  // touches place.name + place.address; rating + review_count for the
  // identity row come from snapshotMap (chain source snapshots).
  const fakePlace: PlaceBasic = {
    name: r.chain_name ?? 'Chain',
    address: `${r.location_count} location${r.location_count === 1 ? '' : 's'} in chain`,
  } as unknown as PlaceBasic;

  const placeLetterGrade = period?.place_letter_grade ?? '—';
  const placePercentile = period?.place_percentile ?? 0;

  const borderColor = (() => {
    const g = placeLetterGrade;
    if (g.includes('A')) return 'border-green-600';
    if (g.includes('B')) return 'border-green-400';
    if (g.includes('C')) return 'border-yellow-400';
    return 'border-red-500';
  })();

  const rangeEnd = new Date(r.created_at + 'Z').toLocaleDateString();

  const opportunityPeriods = r.periods.filter(
    (candidate) => candidate.range === (currentRange?.id ?? '180-days'),
  );
  const headerOpportunities = getScorecardOpportunities(
    opportunityPeriods,
    rangeIdToDays[currentRange?.id ?? '180-days'] ?? 180,
  );

  const content = (
    <Grid
      gridDefinition={[
        { colspan: { default: 12 } },
        { colspan: { default: 12 } },
        { colspan: { default: 12 } },
      ]}
    >
      {period ? (
        <SpaceBetween direction="vertical" size="l">
          <Header variant="h2">Past {currentRange?.text ?? '180 Days'}</Header>
          <Grid
            gridDefinition={[
              { colspan: { default: 12, xs: 3 } },
              { colspan: { default: 12, xs: 3 } },
              { colspan: { default: 12, xs: 3 } },
              { colspan: { default: 12, xs: 3 } },
            ]}
          >
            <StatisticBox
              statistic={period.review_count.toLocaleString()}
              percentile={period.review_count_percentile}
              status={period.review_count_status as StatisticStatus}
              details={`${((period.review_count / (rangeIdToDays[currentRange?.id ?? '180-days'] ?? 180)) * 30).toFixed(0)} / month per location`}
              icon="contact"
              label="Reviews"
            />
            <StatisticBox
              statistic={period.review_count === 0 ? 'N/A' : period.average_rating.toFixed(2)}
              percentile={period.average_rating_percentile}
              status={period.average_rating_status as StatisticStatus}
              icon="star"
              label="Average Rating"
              trend={
                period.review_count === 0
                  ? null
                  : getRatingTrend(
                      period.average_rating,
                      snapshotMap.get(selectedSource ?? '')?.rating,
                    )
              }
            />
            <StatisticBox
              statistic={
                period.review_count === 0
                  ? 'N/A'
                  : (100 * period.response_rate).toFixed(0) + '%'
              }
              percentile={period.response_rate_percentile}
              status={period.response_rate_status as StatisticStatus}
              icon="undo"
              label="Your Response Rate"
            />
            <StatisticBox
              statistic={period.review_count === 0 ? 'N/A' : period.avg_response_time}
              percentile={period.avg_response_time_percentile}
              status={period.avg_response_time_status as StatisticStatus}
              icon="calendar"
              label="Avg Response Time"
            />
          </Grid>
        </SpaceBetween>
      ) : (
        <Container>
          <Box textAlign="center" color="text-status-inactive">
            No data for {currentSource?.text ?? 'this source'} in the past{' '}
            {currentRange?.text?.toLowerCase() ?? 'period'}.
          </Box>
        </Container>
      )}
      {period?.keywords && period.keywords.length > 0 ? (
        <KeywordList
          header={
            <Header
              variant="h2"
              description={`Frequently used keywords from reviews in the past ${(currentRange?.text ?? '180 days').toLowerCase()} on ${currentSource?.text ?? 'Google'}`}
            >
              Frequently Used Keywords
            </Header>
          }
          keywords={(period.keywords ?? []) as ReviewKeyword[]}
          range={(currentRange?.text ?? '180 days').toLowerCase()}
        />
      ) : (
        <div />
      )}
      {filteredReviews.length > 0 ? (
        <ReviewList
          reviews={filteredReviews}
          placeName={r.chain_name ?? 'Chain'}
          header={
            <Header
              variant="h2"
              description={`Negative reviews aggregated across the chain in the past ${(currentRange?.text ?? '180 days').toLowerCase()} on ${currentSource?.text ?? 'Google'}`}
            >
              Negative Reviews
            </Header>
          }
        />
      ) : (
        <div />
      )}
    </Grid>
  );

  return (
    <ViewLayout
      header={<ReportCardHeader />}
      contentType="dashboard"
      navigationHide
      headerVariant="high-contrast"
    >
      <Grid
        gridDefinition={[
          { colspan: { default: 12 } },
          { colspan: { default: 12 } },
          { colspan: { default: 12 } },
        ]}
      >
        <Container
          header={
            <Header
              variant="h1"
              description={`Created ${rangeEnd}`}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button href="#chain-locations" disabled={locationItems.length === 0}>
                    View all locations
                  </Button>
                  <ReportDownloadButton
                    item={{
                      uuid: r.uuid,
                      created_at: r.created_at,
                      place: {
                        name: r.chain_name ?? 'Chain',
                        // No address on a chain — leave undefined so the
                        // filename builder skips that segment.
                      },
                    }}
                    period={selectedRange}
                    variant="button"
                    defaultSource="combined"
                    downloadFn={downloadChainReportPdf}
                  />
                </SpaceBetween>
              }
            >
              Chain Reputation Scorecard
            </Header>
          }
        >
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-stretch gap-4">
            <div className="flex min-w-0 flex-col order-1 lg:flex-1">
              <ScorecardHeader
                placeLetterGrade={placeLetterGrade}
                placePercentile={placePercentile}
                place={fakePlace}
                snapshotMap={snapshotMap}
                borderColor={borderColor}
                source={currentSource?.id}
              />
              <div className="hidden lg:block mt-auto pt-2">
                <SegmentedControl
                  selectedId={selectedRange}
                  onChange={({ detail }) => setSelectedRange(detail.selectedId)}
                  label="View past"
                  options={rangeOptions}
                />
              </div>
            </div>
            <div className="order-2 flex min-w-0 flex-col gap-4 lg:order-3 lg:w-[23rem] lg:shrink-0 xl:w-[28rem]">
              {/* Per-source overall stats list — same component place
                  scorecards use in the upper-right slot. Synthesized
                  snapshotMap drives the rating/review-count display. */}
              <SourceRatingsSummary
                place={fakePlace}
                snapshotMap={snapshotMap}
                onSourceChange={(source: string) => setSelectedSource(source)}
              />
              {headerOpportunities.length > 0 ? (
                <ScorecardOpportunities opportunities={headerOpportunities} />
              ) : null}
            </div>
            <div className="order-3 lg:hidden">
              <SegmentedControl
                selectedId={selectedRange}
                onChange={({ detail }) => setSelectedRange(detail.selectedId)}
                label="View past"
                options={rangeOptions}
              />
            </div>
          </div>
        </Container>
        <Tabs
          activeTabId={selectedSource ?? sourceOptions[0]?.id ?? 'google'}
          onChange={({ detail }) => setSelectedSource(detail.activeTabId)}
          tabs={sourceOptions.map((option) => ({
            label: (
              <SpaceBetween direction="horizontal" size="xxs">
                {option.iconSvg ? <Icon svg={option.iconSvg as React.ReactElement} /> : null}
                <Box variant="span">{option.text}</Box>
              </SpaceBetween>
            ),
            id: option.id,
            disabled: option.disabled,
            content,
          }))}
        />
        <div id="chain-locations">
          <Container
            header={
              <Header
                variant="h2"
                counter={`(${locationItems.length})`}
                description="Locations included in this scorecard snapshot."
              >
                Locations
              </Header>
            }
          >
            {locationItems.length === 0 ? (
              <Box textAlign="center" color="inherit">
                <b>No locations included</b>
                <Box variant="p" color="inherit">
                  This scorecard snapshot does not include any locations.
                </Box>
              </Box>
            ) : (
              <div className="chain-location-list" data-testid="chain-location-list">
                <div className="chain-location-list__header" aria-hidden="true">
                  <span>Location</span>
                  <span>Address</span>
                  <span>Source scorecard</span>
                </div>
                {locationItems.map((item) => (
                  <div
                    className="chain-location-list__row"
                    key={`${item.place_name}-${item.source_scorecard_url ?? ''}`}
                  >
                    <div className="chain-location-list__cell">
                      <span className="chain-location-list__label">Location</span>
                      <span className="chain-location-list__value">{item.place_name}</span>
                    </div>
                    <div className="chain-location-list__cell">
                      <span className="chain-location-list__label">Address</span>
                      <span className="chain-location-list__value">
                        {item.place_address ?? '—'}
                      </span>
                    </div>
                    <div className="chain-location-list__cell">
                      <span className="chain-location-list__label">Source scorecard</span>
                      <span className="chain-location-list__value">
                        {item.source_scorecard_url ? (
                          <Link href={item.source_scorecard_url}>View scorecard</Link>
                        ) : (
                          '—'
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Container>
        </div>
      </Grid>
    </ViewLayout>
  );
}
